/**
 * ROI / EBITDA feed models for the Ryn's Roost dashboard (WI #31083).
 *
 * Source: RynBridge ryn-db endpoints over the prod Ryn DB `RynRoiEntry` table.
 *  - `GET /ryn-db/roi-entries/summary` -> {@link RoiSummary} (all-time counted rollup:
 *    IsActive=1, EntryType run or aggregate, and Provenance IS NULL or the row is
 *    priced (`$.calc.calc` set); WI 37306).
 *  - `GET /ryn-db/roi-entries`         -> {@link RoiEntry}[] (one row per claim; the
 *    per-row EntryDate feed the dashboard buckets client-side for the window view
 *    and freshness stamp, since the summary carries no window/newest field).
 *
 * HONESTY FIREWALL (mirrors the backend, load-bearing): there is deliberately NO
 * single `totalDollarValue`. A cross-value-class dollar sum re-merges
 * `pipeline_visible` (claimed pipeline, not cash) with `hard_dollar` / decision
 * exposure into one headline number. Dollars are exposed ONLY per value class
 * ({@link RoiValueClassRow}); the dashboard renders them as separate labeled tiles
 * and never sums them. Hours saved is the honest headline metric.
 *
 * Numbers arrive as JSON numbers from the function's camelCase serializer, but the
 * normalizers coerce defensively (SQL DECIMAL can surface as a string on other
 * paths) via {@link toNum} so a stray string never poisons a chart with NaN.
 */

/** A safe numeric coercion: null/undefined/'' -> fallback; anything else -> Number or fallback. */
export function toNum(value: unknown, fallback = 0): number {
  if (value === null || value === undefined || value === '') return fallback;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Per-value-class dollar/hours rollup. Dollars stay class-scoped, never summed. */
export interface RoiValueClassRow {
  valueClass: string;
  entryCount: number;
  hoursSaved: number;
  dollarValue: number;
  verifiedValue: number;
}

/** Per-authoring-lane rollup (the by-seat leaderboard dimension). */
export interface RoiInstanceRow {
  rynInstanceId: string;
  /** Write-time-derived role (WI 31106); null on rows predating the RynBridge deploy. */
  rynRole: string | null;
  entryCount: number;
  hoursSaved: number;
  dollarValue: number;
  verifiedValue: number;
}

/**
 * Per-ROLE rollup (WI 31106, Tim's ruling: attribute by role, not by seat, so all the
 * code seats share credit for ADO work). '(unattributed)' rows stay visible as their
 * own honest bucket, never redistributed into a role.
 */
export interface RoiRoleRow {
  rynRole: string;
  entryCount: number;
  hoursSaved: number;
  dollarValue: number;
  verifiedValue: number;
}

export interface RoiCategoryRow {
  category: string;
  entryCount: number;
  hoursSaved: number;
  dollarValue: number;
}

export interface RoiContributorRow {
  personId: number | null;
  personDisplayName: string | null;
  entryCount: number;
  hoursSaved: number;
  dollarValue: number;
}

/** Recurring-automation projection (a separate time-class, never summed with runs). */
export interface RoiSkillRow {
  sourceSkill: string;
  adoptionTier: string;
  entryCount: number;
  projectedAnnualValue: number;
  runsPerYear: number;
}

export interface RoiRecurringSummary {
  realizedAnnual: number;
  potentialAnnual: number;
  bySkill: RoiSkillRow[];
}

/** The flock-infrastructure share of the counted rows (`$.calc.flockInfra`, WI 37306). */
export interface RoiFlockInfraSplit {
  entryCount: number;
  hoursSaved: number;
  devDollarValue: number;
}

/** The `/ryn-db/roi-entries/summary` payload. NOTE: no `totalDollarValue` by design. */
export interface RoiSummary {
  totalEntries: number;
  totalHoursSaved: number;
  calendarDays: number;
  avgHoursSavedPerDay: number;
  /** dev_hours_avoided dollars only (one class, never a cross-class sum). 0 on older backends. */
  devDollarValue: number;
  /** Zeros on payloads from a backend that predates WI 37306. */
  flockInfra: RoiFlockInfraSplit;
  categories: RoiCategoryRow[];
  topContributors: RoiContributorRow[];
  byValueClass: RoiValueClassRow[];
  byRynInstance: RoiInstanceRow[];
  /** Empty on payloads from a backend that predates WI 31106's byRole rollup. */
  byRole: RoiRoleRow[];
  recurringSummary: RoiRecurringSummary;
}

/**
 * One `/ryn-db/skill-roi` row (WI 31106 Skill ROI arm): a skill's Tim-editable
 * projection inputs. hours/year = minutesPerRun x runsPerYear / 60, computed
 * client-side so an inline edit recomputes live before the PUT round-trips.
 * PER-SKILL VALUES ONLY, no per-seat override (Tim, 2026-08-29).
 */
export interface SkillRoi {
  skillRoiId: string;
  skillName: string;
  owningSeat: string | null;
  owningRole: string | null;
  serves: string | null;
  cadence: string | null;
  isRecurring: boolean;
  minutesPerRun: number;
  runsPerYear: number;
  notes: string | null;
}

/** The sparse PUT body for `/ryn-db/skill-roi/{id}`: send only what changed. */
export interface SkillRoiPatch {
  minutesPerRun?: number;
  runsPerYear?: number;
  cadence?: string;
  isRecurring?: boolean;
  notes?: string;
}

/**
 * One `/ryn-db/roi-entries` list row, trimmed to the fields the dashboard buckets.
 * `entryDate` is a naive UTC wall-time string with NO offset suffix
 * ("2026-08-26T18:40:33.046") -- parse it as UTC (parseUtcMs), never local.
 * The counted feed matches the summary's filter exactly (WI 37306): `entryType` in
 * (null, 'run', 'aggregate') AND (`provenance === null` OR the row is priced, `calc`
 * set). An unpriced non-null `provenance` marks a backfilled/derived or draft row;
 * `entryType === 'recurring_skill'` marks a forward-looking automation projection (a
 * separate time-class), and both are excluded so they never inflate realized counts.
 * `entryType` is null on payloads from a backend that predates the column in the
 * list SELECT; null is treated as 'run' (the table default), so the filter is
 * forward-compatible across the RynBridge deploy.
 */
export interface RoiEntry {
  rynRoiEntryId: string;
  entryDate: string | null;
  hoursSaved: number | null;
  dollarValue: number | null;
  provenance: string | null;
  entryType: string | null;
  valueClass: string | null;
  rynInstanceId: string | null;
  /** Write-time-derived role (WI 31106); null on unattributed or pre-deploy rows. */
  rynRole: string | null;
  category: string | null;
  /** T-shirt size from the sizing sweep (WI 31174); null until sized. */
  size: string | null;
  /** Tim's hours for the size (S1/M2/L8/XL16/XXL40/3XL160); null until sized. */
  sizedHours: number | null;
  /** How the size was decided (tag / byte-preserving-split / new-surface / files-heuristic). */
  sizeSource: string | null;
  /** What the entry is (PR title / task line), for the drill-down rows (WI 31176). */
  taskDescription: string | null;
  /** Evidence method (harvest:ado_pr, hours_x_title_rate, ...), the drill-down's source column. */
  method: string | null;
  /** Link to the evidence (PR / ticket), rendered as the drill-down row link. */
  drillDownLink: string | null;
  /**
   * The pricing pass that valued the row (MethodDetailsJson `$.calc.calc`, e.g.
   * 'roi-calc-1031'); null = not priced. A priced row counts whatever its provenance (WI 37306).
   * Optional so hand-built fixtures and older payloads stay valid.
   */
  calc?: string | null;
  /** MethodDetailsJson `$.calc.flockInfra`: true = flock infrastructure work; null = unmarked. */
  flockInfra?: boolean | null;
}

function str(value: unknown): string | null {
  return value === null || value === undefined ? null : String(value);
}

/**
 * The `calc` block of a row's MethodDetailsJson (a JSON string on the wire; an object is
 * accepted too). Malformed or absent JSON reads as no calc, never a thrown error, so one bad
 * row cannot blank the dashboard.
 */
function calcBlock(methodDetails: unknown): Record<string, unknown> | null {
  let parsed: unknown = methodDetails;
  if (typeof methodDetails === 'string') {
    try {
      parsed = JSON.parse(methodDetails);
    } catch {
      return null;
    }
  }
  if (parsed === null || typeof parsed !== 'object') return null;
  const calc = (parsed as Record<string, unknown>)['calc'];
  return calc !== null && typeof calc === 'object'
    ? (calc as Record<string, unknown>)
    : null;
}

export function normalizeRoiValueClassRow(
  raw: Record<string, unknown>
): RoiValueClassRow {
  return {
    valueClass: String(raw['valueClass'] ?? '(unclassified)'),
    entryCount: toNum(raw['entryCount']),
    hoursSaved: toNum(raw['hoursSaved']),
    dollarValue: toNum(raw['dollarValue']),
    verifiedValue: toNum(raw['verifiedValue']),
  };
}

export function normalizeRoiInstanceRow(
  raw: Record<string, unknown>
): RoiInstanceRow {
  return {
    rynInstanceId: String(raw['rynInstanceId'] ?? '(unattributed)'),
    rynRole: str(raw['rynRole']),
    entryCount: toNum(raw['entryCount']),
    hoursSaved: toNum(raw['hoursSaved']),
    dollarValue: toNum(raw['dollarValue']),
    verifiedValue: toNum(raw['verifiedValue']),
  };
}

export function normalizeRoiRoleRow(raw: Record<string, unknown>): RoiRoleRow {
  return {
    rynRole: String(raw['rynRole'] ?? '(unattributed)'),
    entryCount: toNum(raw['entryCount']),
    hoursSaved: toNum(raw['hoursSaved']),
    dollarValue: toNum(raw['dollarValue']),
    verifiedValue: toNum(raw['verifiedValue']),
  };
}

export function normalizeSkillRoi(raw: Record<string, unknown>): SkillRoi {
  return {
    skillRoiId: String(raw['skillRoiId'] ?? ''),
    skillName: String(raw['skillName'] ?? ''),
    owningSeat: str(raw['owningSeat']),
    owningRole: str(raw['owningRole']),
    serves: str(raw['serves']),
    cadence: str(raw['cadence']),
    isRecurring: raw['isRecurring'] === true || raw['isRecurring'] === 1,
    minutesPerRun: toNum(raw['minutesPerRun']),
    runsPerYear: toNum(raw['runsPerYear']),
    notes: str(raw['notes']),
  };
}

export function normalizeRoiSkillRow(
  raw: Record<string, unknown>
): RoiSkillRow {
  return {
    sourceSkill: String(raw['sourceSkill'] ?? '(unattributed)'),
    adoptionTier: String(raw['adoptionTier'] ?? 'UNCONFIRMED'),
    entryCount: toNum(raw['entryCount']),
    projectedAnnualValue: toNum(raw['projectedAnnualValue']),
    runsPerYear: toNum(raw['runsPerYear']),
  };
}

export function normalizeRoiSummary(
  raw: Record<string, unknown> | null
): RoiSummary {
  const r = raw ?? {};
  const rec = (r['recurringSummary'] ?? {}) as Record<string, unknown>;
  const infra = (r['flockInfra'] ?? {}) as Record<string, unknown>;
  const asRows = (v: unknown) =>
    Array.isArray(v) ? (v as Record<string, unknown>[]) : [];
  return {
    totalEntries: toNum(r['totalEntries']),
    totalHoursSaved: toNum(r['totalHoursSaved']),
    calendarDays: toNum(r['calendarDays']),
    avgHoursSavedPerDay: toNum(r['avgHoursSavedPerDay']),
    devDollarValue: toNum(r['devDollarValue']),
    flockInfra: {
      entryCount: toNum(infra['entryCount']),
      hoursSaved: toNum(infra['hoursSaved']),
      devDollarValue: toNum(infra['devDollarValue']),
    },
    categories: asRows(r['categories']).map((c) => ({
      category: String(c['category'] ?? '(uncategorized)'),
      entryCount: toNum(c['entryCount']),
      hoursSaved: toNum(c['hoursSaved']),
      dollarValue: toNum(c['dollarValue']),
    })),
    topContributors: asRows(r['topContributors']).map((c) => ({
      personId:
        c['personId'] === null || c['personId'] === undefined
          ? null
          : toNum(c['personId']),
      personDisplayName: str(c['personDisplayName']),
      entryCount: toNum(c['entryCount']),
      hoursSaved: toNum(c['hoursSaved']),
      dollarValue: toNum(c['dollarValue']),
    })),
    byValueClass: asRows(r['byValueClass']).map(normalizeRoiValueClassRow),
    byRynInstance: asRows(r['byRynInstance']).map(normalizeRoiInstanceRow),
    byRole: asRows(r['byRole']).map(normalizeRoiRoleRow),
    recurringSummary: {
      realizedAnnual: toNum(rec['realizedAnnual']),
      potentialAnnual: toNum(rec['potentialAnnual']),
      bySkill: asRows(rec['bySkill']).map(normalizeRoiSkillRow),
    },
  };
}

export function normalizeRoiEntry(raw: Record<string, unknown>): RoiEntry {
  const calc = calcBlock(raw['methodDetailsJson']);
  const calcId = calc?.['calc'];
  const infra = calc?.['flockInfra'];
  return {
    rynRoiEntryId: String(raw['rynRoiEntryId'] ?? ''),
    entryDate: str(raw['entryDate']),
    hoursSaved:
      raw['hoursSaved'] === null || raw['hoursSaved'] === undefined
        ? null
        : toNum(raw['hoursSaved']),
    dollarValue:
      raw['dollarValue'] === null || raw['dollarValue'] === undefined
        ? null
        : toNum(raw['dollarValue']),
    provenance: str(raw['provenance']),
    entryType: str(raw['entryType']),
    valueClass: str(raw['valueClass']),
    rynInstanceId: str(raw['rynInstanceId']),
    rynRole: str(raw['rynRole']),
    category: str(raw['category']),
    size: str(raw['size']),
    sizedHours:
      raw['sizedHours'] === null || raw['sizedHours'] === undefined
        ? null
        : toNum(raw['sizedHours']),
    sizeSource: str(raw['sizeSource']),
    taskDescription: str(raw['taskDescription']),
    method: str(raw['method']),
    drillDownLink: str(raw['drillDownLink']),
    calc:
      calcId === null || calcId === undefined || calcId === ''
        ? null
        : String(calcId),
    flockInfra:
      infra === true || infra === 'true'
        ? true
        : infra === false || infra === 'false'
          ? false
          : null,
  };
}
